import type { EntitlementKey } from '@datarecs/data-models-core-api';

/** How a usage value should be formatted for display. */
export type EntitlementUnit = 'count' | 'bytes' | 'rps' | 'minutes';

/** A logical grouping of entitlements, so the page reads as sections rather than a flat wall of meters. */
export type EntitlementGroup = 'Resources' | 'Execution' | 'Storage & rate limits';

/** Presentation metadata for a single entitlement. Display-only — the values come from the API. */
export interface EntitlementMeta {
    /** The canonical snake_case key (matches the `EntitlementKey` enum value). */
    readonly key: EntitlementKey;
    /** Human-friendly label shown to the customer. */
    readonly label: string;
    /** Short helper sentence describing what is metered. */
    readonly description: string;
    /** Material Symbols icon name. */
    readonly icon: string;
    /** How `current`/`limit` should be formatted. */
    readonly unit: EntitlementUnit;
    /** Which section of the page this entitlement belongs to. */
    readonly group: EntitlementGroup;
}

/**
 * Display metadata for every entitlement key the platform meters, keyed by the canonical
 * snake_case `EntitlementKey` value. Ordered within each group for a stable, readable layout.
 *
 * This is purely presentational (labels, units, icons, grouping). The authoritative list of
 * keys, and the live current/limit numbers, come from `@datarecs/data-models-core-api` and the
 * `GET /billing/usage` response respectively — never hardcoded here.
 */
export const ENTITLEMENT_META: Record<string, EntitlementMeta> = {
    max_users: {
        key: 'max_users' as EntitlementKey,
        label: 'Team members',
        description: 'Users with access to this tenant.',
        icon: 'groups',
        unit: 'count',
        group: 'Resources'
    },
    max_connections: {
        key: 'max_connections' as EntitlementKey,
        label: 'Connections',
        description: 'Configured database connections.',
        icon: 'database',
        unit: 'count',
        group: 'Resources'
    },
    max_jobs: {
        key: 'max_jobs' as EntitlementKey,
        label: 'Jobs',
        description: 'Reconciliation jobs defined.',
        icon: 'fact_check',
        unit: 'count',
        group: 'Resources'
    },
    max_schedules: {
        key: 'max_schedules' as EntitlementKey,
        label: 'Schedules',
        description: 'Active job schedules.',
        icon: 'schedule',
        unit: 'count',
        group: 'Resources'
    },
    max_api_keys: {
        key: 'max_api_keys' as EntitlementKey,
        label: 'API keys',
        description: 'Active programmatic-access keys.',
        icon: 'vpn_key',
        unit: 'count',
        group: 'Resources'
    },

    max_concurrent_runs: {
        key: 'max_concurrent_runs' as EntitlementKey,
        label: 'Concurrent runs',
        description: 'Runs executing at the same time.',
        icon: 'sync',
        unit: 'count',
        group: 'Execution'
    },
    max_runs_per_day: {
        key: 'max_runs_per_day' as EntitlementKey,
        label: 'Runs per day',
        description: 'Job runs started in the current day.',
        icon: 'today',
        unit: 'count',
        group: 'Execution'
    },
    max_runs_per_month: {
        key: 'max_runs_per_month' as EntitlementKey,
        label: 'Runs per month',
        description: 'Job runs started in the current month.',
        icon: 'calendar_month',
        unit: 'count',
        group: 'Execution'
    },
    min_schedule_interval_minutes: {
        key: 'min_schedule_interval_minutes' as EntitlementKey,
        label: 'Minimum schedule interval',
        description: 'Shortest gap allowed between scheduled runs.',
        icon: 'timer',
        unit: 'minutes',
        group: 'Execution'
    },

    max_storage_bytes: {
        key: 'max_storage_bytes' as EntitlementKey,
        label: 'Artifact storage',
        description: 'Run artifacts stored for this tenant.',
        icon: 'storage',
        unit: 'bytes',
        group: 'Storage & rate limits'
    },
    max_api_rps: {
        key: 'max_api_rps' as EntitlementKey,
        label: 'API rate limit',
        description: 'Sustained API requests per second.',
        icon: 'speed',
        unit: 'rps',
        group: 'Storage & rate limits'
    },
    max_concurrent_downloads: {
        key: 'max_concurrent_downloads' as EntitlementKey,
        label: 'Concurrent downloads',
        description: 'Result downloads in flight at once.',
        icon: 'download',
        unit: 'count',
        group: 'Storage & rate limits'
    }
};

/** Render order for the groups on the page. */
export const ENTITLEMENT_GROUP_ORDER: readonly EntitlementGroup[] = [
    'Resources',
    'Execution',
    'Storage & rate limits'
];

/**
 * Entitlements whose `QUOTA_EXCEEDED` is *transient* — the limit clears on its own as in-flight
 * work finishes, so the right next step is "wait, then retry", NOT "remove one / upgrade". These
 * surface as a calm slow-down toast rather than the upgrade modal.
 *
 * `max_concurrent_runs`: hitting it means N runs are already executing; a finishing run frees the
 * slot. Upgrading is a poor suggestion for a momentary ceiling the user clears by waiting.
 * `max_concurrent_downloads`: the same shape for result downloads in flight.
 */
const TRANSIENT_ENTITLEMENT_KEYS: ReadonlySet<string> = new Set<string>([
    'max_concurrent_runs',
    'max_concurrent_downloads'
]);

/** True when an entitlement's limit is a momentary in-flight ceiling the user clears by waiting. */
export function isTransientEntitlement(key: string): boolean {
    return TRANSIENT_ENTITLEMENT_KEYS.has(key);
}

/**
 * Fall back to a readable label for any key the API returns that we don't yet have metadata for
 * (e.g. a new entitlement added server-side before the console ships its label). Turns
 * `max_widget_count` into `Max widget count` rather than showing a raw key.
 */
export function fallbackMeta(key: string): EntitlementMeta {
    const label = key
        .replace(/^max_/, '')
        .replace(/^min_/, 'min ')
        .replace(/_/g, ' ')
        .replace(/^\w/, c => c.toUpperCase());
    return {
        key: key as EntitlementKey,
        label,
        description: '',
        icon: 'tune',
        unit: 'count',
        group: 'Resources'
    };
}

/** Resolve display metadata for a key, falling back gracefully for unknown keys. */
export function metaFor(key: string): EntitlementMeta {
    return ENTITLEMENT_META[key] ?? fallbackMeta(key);
}

/** A power-of-two byte formatter (KiB/MiB/GiB/TiB) — storage limits are expressed in binary units. */
export function formatBytes(bytes: number): string {
    if (bytes <= 0) return '0 B';
    const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'];
    const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, exponent);
    // Whole numbers read cleaner without a trailing .0; otherwise show up to 2 dp.
    const rounded = Number.isInteger(value) ? value.toString() : value.toFixed(value < 10 ? 2 : 1);
    return `${rounded} ${units[exponent]}`;
}

/** Format a usage/limit value according to its unit. Used for both `current` and `limit`. */
export function formatValue(value: number, unit: EntitlementUnit): string {
    switch (unit) {
        case 'bytes':
            return formatBytes(value);
        case 'rps':
            return `${value.toLocaleString()} req/s`;
        case 'minutes':
            return value === 1 ? '1 min' : `${value.toLocaleString()} min`;
        case 'count':
        default:
            return value.toLocaleString();
    }
}
